import styled from '@xstyled/styled-components';
import React, { useEffect, useState } from 'react';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { BUBBLE } from '../itemIds';

import Llama from '~/assets/llamas/llama.bubble.svg';
import ArrowDubble from '~/assets/icons/arrow.bubble.svg';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { shallow } from 'zustand/shallow';
import { useNextSequence } from '~/training/useNextSequence';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { Overlay } from '~/ui/overlay/Overlay';

const selector = (s: MediaPlayerStore) =>
  select(s, 'status', 'toggle', 'progress');

const sequenceSelector = (s: SequenceStore) => select(s, 'blockers');

interface Props {
  title?: string;
  body?: string;
  youtubeUrl?: string;
  acknowledge?: boolean;
}

export type BubbleType = Props;
export type BubbleDefinition = ItemDefinition<typeof BUBBLE, Props>;

const Wrapper = styled.div`
  position: absolute;
  z-index: 10;
  right: 0;
  width: 50%;
  height: 60%;
  top: 35%;

  .llamaImg {
    float: right;
  }

  .arrowRight {
    transform: rotate(261deg);
    right: 160px;
    position: absolute;
  }
  h2 {
    font-family: var(--llama-font);
    font-size: 24px;
    text-align: center;
    padding-bottom: 20px;
  }
  p {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  @media (max-width: sm) {
    top: 55%;

    .arrowRight {
      display: none;
    }
  }
`;

const Union = styled.div`
  width: 100%;
  min-height: 130px;
  background-color: #fff;
  border-radius: 15px;
  padding: 20px;
  position: absolute;
  margin-left: -180px;
  box-shadow: -10px -10px 20px grey;
  max-height: 100%;
  overflow-x: auto;

  .container {
    position: relative;
    width: 85%;
    height: 0;
    padding-bottom: 40%;
    margin-left: auto;
    margin-right: auto;
  }
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  button {
    float: right;
    position: relative;
    top: 0px;
    margin-right: 25px;
  }
  @media (max-width: sm) {
    margin-left: -185px;
    width: 365px;
    margin-top: -300px;
  }
`;

export const BubbleComponent = ({
  title,
  body,
  youtubeUrl,
  acknowledge
}: Props) => {
  const next = useNextSequence();
  const { status } = useMediaPlayerStore(selector, shallow);
  const { blockers } = useSequenceStore(sequenceSelector);
  const [show, setShow] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (
        (status === 'done' || status === 'nomedia') &&
        blockers.length === 0
      ) {
        setShow(true);
      } else {
        setShow(false);
      }
    }, 300);
  }, [status, blockers]);

  if (status !== 'done' && status !== 'nomedia') {
    return null;
  }

  if (blockers.length !== 0) {
    return null;
  }

  return show ? (
    <Overlay open={true}>
      <Wrapper>
        <Llama className="llamaImg" />
        <ArrowDubble className="arrowRight" />
        <Union>
          {acknowledge ? (
            <CloseButton onClick={next} type="close" />
          ) : (
            <CloseButton onClick={() => setShow(false)} type="close" />
          )}
          {title && <h2>{title}</h2>}
          <div className="bubbleContent">
            {youtubeUrl && (
              <div className="container">
                <iframe
                  width="100%"
                  src={youtubeUrl.replace('watch?v=', 'embed/')}
                ></iframe>
              </div>
            )}
            {body && (
              <div
                className="bodyContent"
                dangerouslySetInnerHTML={{ __html: body }}
              ></div>
            )}
          </div>
        </Union>
      </Wrapper>
    </Overlay>
  ) : null;
};

export class Bubble extends Item<typeof BUBBLE, Props> {
  protected render({
    title,
    body,
    youtubeUrl,
    acknowledge
  }: RenderProps<Props>) {
    return (
      <BubbleComponent
        title={title || ''}
        body={body || ''}
        youtubeUrl={youtubeUrl || ''}
        acknowledge={acknowledge || false}
      />
    );
  }
}

registry[BUBBLE] = Bubble;
